if (!customElements.get('column-series-carousel')) {
  class ColumnSeriesCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-series-track]');
      this.nextButton = this.querySelector('[data-series-next]');
      this.progress = this.querySelector('[data-series-progress]');
      this.progressThumb = this.querySelector('[data-series-progress-thumb]');
      this.cards = Array.from(this.querySelectorAll('[data-series-card]'));

      if (!this.track || this.cards.length === 0) return;

      this.initialized = true;
      this.update = this.update.bind(this);
      this.scrollNext = this.scrollNext.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);
      this.preventNativeDrag = this.preventNativeDrag.bind(this);

      this.track.addEventListener('scroll', this.update, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onClick, true);
      this.track.addEventListener('dragstart', this.preventNativeDrag);
      this.nextButton?.addEventListener('click', this.scrollNext);

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.update);
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      this.track?.removeEventListener('pointermove', this.onPointerMove);
      this.track?.removeEventListener('pointerup', this.onPointerUp);
      this.track?.removeEventListener('pointercancel', this.onPointerUp);
      this.track?.removeEventListener('click', this.onClick, true);
      this.track?.removeEventListener('dragstart', this.preventNativeDrag);
      this.nextButton?.removeEventListener('click', this.scrollNext);
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.suppressClick = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.track.scrollLeft;
    }

    onPointerMove(event) {
      if (!this.isPointerDown) return;

      const distance = event.clientX - this.dragStartX;
      if (!this.isDragging) {
        if (Math.abs(distance) <= 4) return;

        this.isDragging = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
      }

      this.track.scrollLeft = this.dragStartScrollLeft - distance;
      event.preventDefault();
    }

    onPointerUp(event) {
      if (!this.isPointerDown) return;

      this.isPointerDown = false;
      this.suppressClick = this.isDragging;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');

      if (this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
    }

    onClick(event) {
      if (!this.suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }

    preventNativeDrag(event) {
      event.preventDefault();
    }

    scrollNext() {
      const firstCard = this.cards[0];
      const secondCard = this.cards[1];
      const distance = secondCard
        ? secondCard.offsetLeft - firstCard.offsetLeft
        : firstCard.getBoundingClientRect().width;

      this.track.scrollBy({ left: distance, behavior: 'smooth' });
    }

    update() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const scrollPosition = Math.min(maxScroll, Math.max(0, this.track.scrollLeft));
      const progressValue = maxScroll === 0 ? 100 : (scrollPosition / maxScroll) * 100;

      if (this.nextButton) {
        this.nextButton.disabled = maxScroll === 0 || scrollPosition >= maxScroll - 1;
      }

      if (!this.progress || !this.progressThumb) return;

      const thumbWidth = Math.min(100, (this.track.clientWidth / this.track.scrollWidth) * 100);
      const thumbLeft = maxScroll === 0 ? 0 : (progressValue / 100) * (100 - thumbWidth);

      this.progressThumb.style.width = `${thumbWidth}%`;
      this.progressThumb.style.left = `${thumbLeft}%`;
      this.progress.setAttribute('aria-valuenow', String(Math.round(progressValue)));
    }
  }

  customElements.define('column-series-carousel', ColumnSeriesCarousel);
}
